import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'zignal-landscape/lib/Modal';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';
import ModalBody from 'zignal-landscape/lib/ModalBody';
import ModalFooter from 'zignal-landscape/lib/ModalFooter';
import Button from 'zignal-landscape/lib/Button';
import Input from 'zignal-landscape/lib/Input';
import Select from 'zignal-landscape/lib/Select';
import Loading from 'zignal-landscape/lib/Loading';
import Label from 'zignal-landscape/lib/Label';
import Save1Icon from 'zignal-landscape/lib/Icon/Save1Icon';

import {
  STORY_FIELDS,
  LAT_MIN,
  LAT_MAX,
  LON_MIN,
  LON_MAX,
  LATLONG_WARNING,
  GEO_LOGIC,
  RESOLUTION_CITY,
} from '../../constants';
import {
  isLatValid,
  isLonValid,
  isFiniteNumber,
  filterNumeric,
  disabledCursor,
  formatGeoResult,
} from '../../defaultLocationsUtils';
import getPlatformOptions from '../../platformOptions';

const INPUT_HEIGHT = '1.75rem'; // 28px — matches input visible box
const MODAL_WIDTH = '19.2rem';

// box-sizing makes landscape Select grow when an option is selected.
// Override singleValue with padding/lineHeight/color overrides.
const selectStyles = {
  singleValue: (base) => ({...base, padding: 0, lineHeight: 'inherit', color: 'inherit'}),
};

// Pull a per-field message out of an error that has the {fieldErrors: {...}}
// shape produced by parseFieldErrors() on the saga side. Plain-string errors
// produce no field-level message.
const fieldErrorOf = (err, field) => (
  err && typeof err === 'object' && err.fieldErrors && err.fieldErrors[field]
);

const FieldError = ({ message }) => (
  message ? <p style={{margin: '4px 0 0', color: '#d9534f', fontSize: '0.85em'}}>{message}</p> : null
);
FieldError.propTypes = { message: PropTypes.string };

const CreateDefaultLocationModal = ({
  isOpen = false,
  onClose,
  onSubmit,
  onLookupForward,
  onLookupReverse,
  onClearLookup,
  lookupResults = [],
  isLookingUp = false,
  lookupError = null,
  isCreating = false,
  createError = null,
  defaultPlatform = '',
}) => {
  const platformOptions = React.useMemo(() => getPlatformOptions(), []);
  const [term, setTerm] = useState('');
  const defaultPlatformOption = defaultPlatform
    ? platformOptions.find(p => p.value === defaultPlatform) || null
    : null;
  const [platform, setPlatform] = useState(defaultPlatformOption);
  const [storyField, setStoryField] = useState(null);
  const [cityQuery, setCityQuery] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [selectedGeoIdx, setSelectedGeoIdx] = useState(-1);

  React.useEffect(() => {
    if (isOpen && defaultPlatform) {
      const opt = platformOptions.find(p => p.value === defaultPlatform) || null;
      setPlatform(opt);
    }
  }, [isOpen, defaultPlatform, platformOptions]);

  // Reset transient lookup/picker state whenever the modal closes.
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedGeoIdx(-1);
      onClearLookup && onClearLookup();
    }
  }, [isOpen, onClearLookup]);

  // A new lookupResults array invalidates the previous selection.
  // If exactly one result, auto-select it for the user.
  React.useEffect(() => {
    setSelectedGeoIdx(lookupResults.length === 1 ? 0 : -1);
  }, [lookupResults]);

  const resetAfterSubmit = () => {
    setTerm('');
    setCityQuery('');
    setLatitude('');
    setLongitude('');
    setSelectedGeoIdx(-1);
    onClearLookup && onClearLookup();
  };

  const handleClose = () => {
    setTerm('');
    setPlatform(null);
    setStoryField(null);
    setCityQuery('');
    setLatitude('');
    setLongitude('');
    setSelectedGeoIdx(-1);
    onClose();
  };

  const hasLatLon = latitude !== '' || longitude !== '';
  const latLonNumeric = isFiniteNumber(latitude) && isFiniteNumber(longitude);
  const latLonInRange = isLatValid(latitude) && isLonValid(longitude);
  const latLonReady = latLonNumeric && latLonInRange;
  const latRangeError = latitude !== '' && isFiniteNumber(latitude) && !isLatValid(latitude);
  const lonRangeError = longitude !== '' && isFiniteNumber(longitude) && !isLonValid(longitude);
  const cityHasText = cityQuery.trim().length > 0;
  // Single Search button: enabled when either side has actionable input.
  const canSearch = !isLookingUp && (
    (cityHasText && !hasLatLon) ||
    (latLonReady && !cityHasText)
  );

  const selectedGeo = selectedGeoIdx >= 0 && selectedGeoIdx < lookupResults.length
    ? lookupResults[selectedGeoIdx]
    : null;
  const canProceed = term.trim() && platform && storyField && !!selectedGeo;

  const runSearch = () => {
    if (!canSearch) {return;}
    setSelectedGeoIdx(-1);
    if (cityHasText) {
      onLookupForward(cityQuery.trim());
    } else {
      onLookupReverse(Number(latitude), Number(longitude));
    }
  };

  const handleCityKeyDown = (e) => {
    if (e.key === 'Enter' && canSearch && cityHasText) {runSearch();}
  };

  // Editing any location input invalidates a previously-fetched picker list.
  const invalidatePicker = () => {
    if (lookupResults.length > 0 || selectedGeoIdx >= 0) {
      setSelectedGeoIdx(-1);
      onClearLookup && onClearLookup();
    }
  };

  const submitForm = (options) => {
    if (!canProceed) {return;}
    const location = {
      ...selectedGeo,
      resolution: RESOLUTION_CITY,
      logic: GEO_LOGIC,
    };
    onSubmit({
      term,
      platform: platform ? platform.value : '',
      story_field: storyField ? storyField.value : '',
      location,
    }, options);
  };

  const handleSubmit = () => submitForm();

  const handleSaveAndAddAnother = () => {
    if (!canProceed) {return;}
    submitForm({ keepModalOpen: true });
    resetAfterSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      size="small"
      onEscapePress={handleClose}
      onBackdropClick={handleClose}
      style={{maxWidth: MODAL_WIDTH, width: MODAL_WIDTH}}
    >
      <ModalHeader hideCloseButton={false} onClose={handleClose}>
        Create Default Location
      </ModalHeader>

      <ModalBody>
        <div>
          <div style={{marginBottom: '12px'}}>
            <Label>Term</Label>
            <Input
              id="dl-term"
              placeholder="Enter term"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            <FieldError message={fieldErrorOf(createError, 'term')} />
          </div>

          <div style={{marginBottom: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
            <div>
              <Label>Platform</Label>
              <Select
                id="dl-platform"
                options={platformOptions}
                value={platform}
                onChange={setPlatform}
                placeholder="Select platform"
                selectStyles={selectStyles}
              />
              <FieldError message={fieldErrorOf(createError, 'platform')} />
            </div>
            <div>
              <Label>Field</Label>
              <Select
                id="dl-story-field"
                options={STORY_FIELDS}
                value={storyField}
                onChange={setStoryField}
                placeholder="Select field"
                selectStyles={selectStyles}
              />
              <FieldError message={fieldErrorOf(createError, 'story_field')} />
            </div>
          </div>

          <Label>Location</Label>
          <div
            style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '12px',
              backgroundColor: '#fafafa',
            }}
          >
            <div style={{marginBottom: '8px', ...disabledCursor(hasLatLon)}}>
              <Input
                id="dl-location-city"
                placeholder="e.g. San Francisco, CA"
                value={cityQuery}
                onChange={(e) => { setCityQuery(e.target.value); invalidatePicker(); }}
                onKeyDown={handleCityKeyDown}
                disabled={hasLatLon}
                style={disabledCursor(hasLatLon)}
              />
            </div>

            <div style={{margin: '8px 0', color: '#666', fontSize: '0.9em'}}>Or</div>

            <div style={{display: 'grid', gridTemplateColumns: 'minmax(0, 7.2em) minmax(0, 7.2em)', gap: '8px', alignItems: 'end'}}>
              <div style={disabledCursor(cityHasText)}>
                <Label>Latitude</Label>
                <Input
                  id="dl-location-lat"
                  type="text"
                  inputMode="decimal"
                  value={latitude}
                  onChange={(e) => { setLatitude(filterNumeric(e.target.value, latitude)); invalidatePicker(); }}
                  disabled={cityHasText}
                  style={{height: INPUT_HEIGHT, boxSizing: 'border-box', ...disabledCursor(cityHasText)}}
                />
              </div>
              <div style={disabledCursor(cityHasText)}>
                <Label>Longitude</Label>
                <Input
                  id="dl-location-lon"
                  type="text"
                  inputMode="decimal"
                  value={longitude}
                  onChange={(e) => { setLongitude(filterNumeric(e.target.value, longitude)); invalidatePicker(); }}
                  disabled={cityHasText}
                  style={{height: INPUT_HEIGHT, boxSizing: 'border-box', ...disabledCursor(cityHasText)}}
                />
              </div>
            </div>

            {(latRangeError || lonRangeError) && (
              <p style={{margin: '8px 0 0', color: '#d9534f', fontSize: '0.85em'}}>
                {latRangeError && `Latitude must be between ${LAT_MIN} and ${LAT_MAX}. `}
                {lonRangeError && `Longitude must be between ${LON_MIN} and ${LON_MAX}.`}
              </p>
            )}

            {hasLatLon && (
              <p style={{margin: '8px 0 0', color: '#8a6d3b', fontSize: '0.85em'}}>
                {LATLONG_WARNING}
              </p>
            )}

            <div style={{marginTop: '12px'}}>
              <Button
                buttonStyle="primary"
                disabled={!canSearch}
                onClick={runSearch}
                style={disabledCursor(!canSearch)}
              >
                Search
              </Button>
            </div>
          </div>

          {isLookingUp && (
            <div style={{textAlign: 'center', padding: '8px'}}>
              <Loading />
            </div>
          )}

          {!isLookingUp && lookupError && (
            <p style={{margin: '8px 0', color: '#d9534f', fontSize: '0.85em'}}>{lookupError}</p>
          )}

          {!isLookingUp && lookupResults.length > 0 && (
            <ul style={{listStyle: 'none', padding: 0, margin: '8px 0', maxHeight: '160px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px'}}>
              {lookupResults.map((geo, idx) => (
                <li key={idx} style={{padding: '6px 10px', borderBottom: idx < lookupResults.length - 1 ? '1px solid #eee' : 'none'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                    <input
                      type="radio"
                      name="dl-geo-result"
                      checked={selectedGeoIdx === idx}
                      onChange={() => setSelectedGeoIdx(idx)}
                    />
                    <span>{formatGeoResult(geo)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          <FieldError message={fieldErrorOf(createError, 'location')} />

          {typeof createError === 'string' && createError && (
            <p style={{color: '#d9534f', marginTop: '8px'}}>{createError}</p>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={{display: 'flex', gap: '8px'}}>
            <Button
              buttonStyle="positive"
              onClick={handleSubmit}
              disabled={!canProceed || isCreating}
              style={disabledCursor(!canProceed || isCreating)}
            >
              <Save1Icon width={14} height={14} /> Save
            </Button>
            <Button buttonStyle="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </div>
          <div>
            <Button
              buttonStyle="positive"
              disabled={!canProceed || isCreating}
              onClick={handleSaveAndAddAnother}
              style={disabledCursor(!canProceed || isCreating)}
            >
              <Save1Icon width={14} height={14} /> Save &amp; Add Another
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

CreateDefaultLocationModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onLookupForward: PropTypes.func.isRequired,
  onLookupReverse: PropTypes.func.isRequired,
  onClearLookup: PropTypes.func,
  lookupResults: PropTypes.array,
  isLookingUp: PropTypes.bool,
  lookupError: PropTypes.string,
  isCreating: PropTypes.bool,
  createError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  defaultPlatform: PropTypes.string,
};

export default CreateDefaultLocationModal;
